import type { ReactElement } from 'react';
import { useEffect } from 'react';

import { BpkList } from '@skyscanner-internal/backpack-web/bpk-component-list';
import BpkSelect from '@skyscanner-internal/backpack-web/bpk-component-select';
import BpkText, { TEXT_STYLES } from '@skyscanner-internal/backpack-web/bpk-component-text';
import StringsPropVerification from '@web-platform/global-components-higher-order-components/StringsPropVerification';
import { redirect } from 'saddlebag-browser';

import FooterFlagLink from './FooterFlagLink';
import FooterFlagOption from './FooterFlagOption';

import type { Logger } from '@web-platform/global-components-types/utils';

import STYLES from './FooterFlags.module.scss';

const stringsKeys = ['heading', 'selectedOptionLabel', 'ariaChooseAnInternationalSite'] as const;
const componentName = 'footer-flags';

/**
 * @interface
 * @property heading * The title or heading of the component
 * @property selectedOptionLabel *  Label for the selected option in the dropdown
 * @property ariaChooseAnInternationalSite * ARIA label for the dropdown
 */
export type Strings = {
  [key in (typeof stringsKeys)[number]]: string;
};

/**
 * @public
 */

export interface FooterFlagsProps {
  /**
   * An array of objects representing each flag link. Uses provided Data Helper `getFooterFlagsData`
   */
  footerFlagLinks: Array<{
    id: string;
    /**
     * Label displayed in the dropdown for this link
     */
    dropDownItemLabel: Strings['selectedOptionLabel'];
    /**
     * Text label displayed next to the flag/link
     */
    linkLabel: Strings['selectedOptionLabel'];
    /**
     * Market code (e.g., 'US', 'UK')
     */
    market: string;
    /**
     * URL to redirect to when the link/option is clicked
     */
    url: string;
  }>;
  /**
   * A boolean determining if the environment is production.
   * @defaultValue false
   */
  isProductionEnv: boolean;
  /**
   * Logger object
   */
  logger: Logger;
  /**
   * Translated strings for the component
   */
  strings: Strings;
}

/**
 * The `FooterFlags` component displays a list of localised links on other domains for the current page. The markets and domains chosen are those related to the current market.
 *
 * @public
 * @example
 * ```tsx
 * import FooterFlags from '@skyscanner-internal/global-component/footer-flags';
 *
 * const footerFlagsLinksData = await getFooterFlagsData(
 *  allFooterFlagStrings,
 *  allMarkets.Items,
 *  allLocales.Items,
 *  allDomains.Items,
 *  generateUrl,
 *  market,
 *  locale,
 *  fullDomainName,
 *  'space-travel-home',
 *  '',
 *  '',
 *  false,
 *  false,
 *  );
 *
 * const strings = {
 *   heading: 'International Sites',
 *   selectedOptionLabel: 'Please select an option',
 *   ariaChooseAnInternationalSite: 'Choose an international site',
 * };
 *
 * const Layout = () => (
 *   <div>
 *     // Other components
 *     <FooterFlags
 *       footerFlagLinks={footerFlagLinksData}
 *       strings={strings}
 *       logger={logger}
 *       isProductionEnv={true}
 *     />
 *   </div>
 * );
 * ```
 */
const FooterFlags = ({
  footerFlagLinks,
  isProductionEnv = false,
  logger,
  strings,
}: FooterFlagsProps): ReactElement => {
  useEffect(() => {
    logger.logOperationalEvent({
      eventName: 'Rendered',
      component: componentName,
    });
  }, [logger]);

  const handleOptionSelect = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const url = event.target.value;
    if (url.length > 0) {
      logger.logOperationalEvent({
        eventName: 'Mobile Dropdown Selected',
        component: componentName,
      });
      redirect(url);
    }
  };

  return (
    <StringsPropVerification
      stringsKeys={stringsKeys}
      componentName="FooterFlags"
      strings={strings}
      logger={logger}
      isProductionEnv={isProductionEnv}
    >
      <div className={STYLES.FooterFlags}>
        <div>
          <BpkText tagName="h2" textStyle={TEXT_STYLES.heading4}>
            {strings.heading}
          </BpkText>
        </div>
        <BpkList className={STYLES.FooterFlags__list}>
          {footerFlagLinks.map(({ id, linkLabel, market, url }) => (
            <FooterFlagLink key={id} market={market} linkLabel={linkLabel} url={url} />
          ))}
        </BpkList>
        <BpkSelect
          id="FooterFlagsSelect"
          name="FooterFlagsSelect"
          className={STYLES.FooterFlags__dropDownList}
          aria-label={strings.ariaChooseAnInternationalSite}
          value=""
          onChange={handleOptionSelect}
        >
          <FooterFlagOption
            key="option-none"
            url=""
            dropDownItemLabel={strings.selectedOptionLabel}
          />

          {footerFlagLinks.map(({ dropDownItemLabel, id, url }) => (
            <FooterFlagOption key={id} dropDownItemLabel={dropDownItemLabel} url={url} />
          ))}
        </BpkSelect>
      </div>
    </StringsPropVerification>
  );
};

export default FooterFlags;
